'use client'

import clsx from 'clsx'
import { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react'
import { ChevronDownIcon, XMarkIcon, CheckIcon } from '@heroicons/react/20/solid'

// Memoized Option Item component to prevent unnecessary re-renders
const OptionItem = memo(function OptionItem({ option, isSelected, onToggle, labelKey, valueKey }) {
    const optValue = typeof option === 'object' ? option[valueKey] : option
    const optLabel = typeof option === 'object' ? option[labelKey] : option

    return (
        <div
            onClick={() => onToggle(optValue)}
            className={clsx([
                'flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors',
                'hover:bg-zinc-50',
                isSelected && 'bg-primary/5',
            ])}
        >
            <div
                className={clsx([
                    'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border-2 transition-colors',
                    isSelected ? 'border-primary bg-primary' : 'border-zinc-300',
                ])}
            >
                {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
            </div>
            <span className={clsx(
                'truncate text-sm',
                isSelected ? 'font-medium text-zinc-900' : 'text-zinc-600'
            )}>
                {optLabel}
            </span>
        </div>
    )
})

export function MultiSelect({
    className,
    placeholder = 'Select...',
    options = [],
    value = [],
    onChange,
    disabled = false,
    labelKey = 'label',
    valueKey = 'value',
    maxDisplayItems = 2,
    searchable = true,
    'aria-label': ariaLabel,
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [showTooltip, setShowTooltip] = useState(false)
    const containerRef = useRef(null)
    const inputRef = useRef(null)

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false)
                setSearchQuery('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Memoize value set for O(1) lookup
    const valueSet = useMemo(() => new Set(value), [value])

    // Filter options based on search query - memoized
    const filteredOptions = useMemo(() => {
        const query = searchQuery.toLowerCase()
        return options.filter((option) => {
            const label = typeof option === 'object' ? option[labelKey] : option
            return label?.toLowerCase().includes(query)
        })
    }, [options, searchQuery, labelKey])

    // Get all selected labels for tooltip - memoized
    const selectedLabels = useMemo(() => {
        if (!value || value.length === 0) return []
        return value.map((v) => {
            const option = options.find((opt) => 
                (typeof opt === 'object' ? opt[valueKey] : opt) === v
            )
            return option ? (typeof option === 'object' ? option[labelKey] : option) : v
        })
    }, [value, options, valueKey, labelKey])

    // Get display value for the input - memoized
    const displayText = useMemo(() => {
        if (!value || value.length === 0) return null
        if (value.length === 1) {
            return selectedLabels[0]
        }
        return `${value.length} selected`
    }, [value, selectedLabels])

    // Handle option toggle - memoized callback
    const handleToggle = useCallback((optionValue) => {
        const newValue = value.includes(optionValue)
            ? value.filter((v) => v !== optionValue)
            : [...value, optionValue]
        onChange(newValue)
    }, [value, onChange])

    // Handle clear all - memoized callback
    const handleClearAll = useCallback((e) => {
        e.stopPropagation()
        onChange([])
        setSearchQuery('')
    }, [onChange])

    // Handle select all filtered - memoized callback
    const handleSelectAll = useCallback(() => {
        const filteredValues = filteredOptions.map((opt) =>
            typeof opt === 'object' ? opt[valueKey] : opt
        )
        const allSelected = filteredValues.every((v) => valueSet.has(v))
        if (allSelected) {
            onChange(value.filter((v) => !filteredValues.includes(v)))
        } else {
            const newValue = [...new Set([...value, ...filteredValues])]
            onChange(newValue)
        }
    }, [filteredOptions, valueKey, valueSet, value, onChange])

    // Check if all filtered options are selected - memoized
    const allFilteredSelected = useMemo(() => {
        return filteredOptions.length > 0 && 
            filteredOptions.every((opt) => 
                valueSet.has(typeof opt === 'object' ? opt[valueKey] : opt)
            )
    }, [filteredOptions, valueSet, valueKey])

    return (
        <div ref={containerRef} className={clsx('relative', className)}>
            {/* Main Input */}
            <div
                onClick={() => {
                    if (!disabled) {
                        setIsOpen(!isOpen)
                        if (!isOpen && searchable) {
                            setTimeout(() => inputRef.current?.focus(), 50)
                        }
                    }
                }}
                onMouseEnter={() => value.length > 0 && setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                className={clsx([
                    'relative flex h-9 w-full cursor-pointer items-center rounded-lg border bg-white transition-all',
                    'hover:border-zinc-400',
                    isOpen 
                        ? 'border-primary ring-1 ring-primary' 
                        : 'border-zinc-300',
                    disabled && 'cursor-not-allowed bg-zinc-50 opacity-60',
                ])}
            >
                {/* Display Area */}
                <div className="flex flex-1 items-center gap-2 overflow-hidden px-3">
                    {value.length === 0 ? (
                        <span className="truncate text-sm text-zinc-400">{placeholder}</span>
                    ) : (
                        <div className="flex items-center gap-2 overflow-hidden">
                            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded bg-primary px-1.5 text-xs font-semibold text-white">
                                {value.length}
                            </span>
                            <span className="truncate text-sm text-zinc-700">
                                {displayText}
                            </span>
                        </div>
                    )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-0.5 border-l border-zinc-200 px-1.5">
                    {value.length > 0 && (
                        <button
                            type="button"
                            onClick={handleClearAll}
                            className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    )}
                    <div className="p-1 text-zinc-400">
                        <ChevronDownIcon className={clsx('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
                    </div>
                </div>
            </div>

            {/* Tooltip showing all selected items */}
            {showTooltip && value.length > 1 && !isOpen && (
                <div className="absolute bottom-full left-0 z-50 mb-2 max-w-xs rounded-lg border border-primary/20 bg-white px-3 py-2 shadow-lg">
                    <div className="text-xs font-medium text-primary mb-1.5">Selected items:</div>
                    <div className="flex flex-wrap gap-1.5">
                        {selectedLabels.map((label, idx) => (
                            <span
                                key={idx}
                                className="inline-block rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                            >
                                {label}
                            </span>
                        ))}
                    </div>
                    {/* Arrow */}
                    <div className="absolute -bottom-1.5 left-4 h-3 w-3 rotate-45 border-b border-r border-primary/20 bg-white" />
                </div>
            )}

            {/* Dropdown Options */}
            {isOpen && !disabled && (
                <div
                    className={clsx([
                        'absolute z-50 mt-1 w-full rounded-lg bg-white shadow-xl',
                        'border border-zinc-200',
                        'overflow-hidden',
                    ])}
                >
                    {/* Search Input */}
                    {searchable && (
                        <div className="border-b border-zinc-100 p-2">
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full rounded-md border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-sm placeholder:text-zinc-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}

                    {/* Select All Option */}
                    {filteredOptions.length > 1 && (
                        <div
                            onClick={handleSelectAll}
                            className="flex cursor-pointer items-center gap-3 border-b border-zinc-100 px-3 py-2.5 hover:bg-zinc-50"
                        >
                            <div
                                className={clsx([
                                    'flex h-4 w-4 items-center justify-center rounded border-2 transition-colors',
                                    allFilteredSelected
                                        ? 'border-primary bg-primary'
                                        : 'border-zinc-300',
                                ])}
                            >
                                {allFilteredSelected && <CheckIcon className="h-3 w-3 text-white" />}
                            </div>
                            <span className="text-sm font-medium text-zinc-700">
                                {allFilteredSelected ? 'Deselect All' : 'Select All'}
                            </span>
                        </div>
                    )}

                    {/* Options List */}
                    <div className="max-h-52 overflow-y-auto">
                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-4 text-center text-sm text-zinc-400">
                                No options found
                            </div>
                        ) : (
                            filteredOptions.map((option) => {
                                const optValue = typeof option === 'object' ? option[valueKey] : option
                                return (
                                    <OptionItem
                                        key={optValue}
                                        option={option}
                                        isSelected={valueSet.has(optValue)}
                                        onToggle={handleToggle}
                                        labelKey={labelKey}
                                        valueKey={valueKey}
                                    />
                                )
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
